import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Search, Filter } from 'lucide-react';

// Données produits simplifiées - Focus local
const products = {
  fr: {
    fruits: [
      { name: 'Mangue Kent', emoji: '🥭', season: 'Mars - Juin' },
      { name: 'Ananas Sweet', emoji: '🍍', season: 'Toute l\'année' },
      { name: 'Banane Cavendish', emoji: '🍌', season: 'Toute l\'année' },
      { name: 'Papaye Solo', emoji: '🍈', season: 'Toute l\'année' },
      { name: 'Avocat Hass', emoji: '🥑', season: 'Sept - Fév' },
      { name: 'Orange Navel', emoji: '🍊', season: 'Nov - Avril' },
      { name: 'Pomme Golden', emoji: '🍎', season: 'Sept - Mars' },
      { name: 'Raisin', emoji: '🍇', season: 'Juin - Oct' },
      { name: 'Pastèque', emoji: '🍉', season: 'Mars - Sept' },
      { name: 'Melon Charentais', emoji: '🍈', season: 'Mai - Sept' },
      { name: 'Citron', emoji: '🍋', season: 'Toute l\'année' },
      { name: 'Noix de Coco', emoji: '🥥', season: 'Toute l\'année' },
    ],
    legumes: [
      { name: 'Tomate', emoji: '🍅', season: 'Toute l\'année' },
      { name: 'Oignon', emoji: '🧅', season: 'Toute l\'année' },
      { name: 'Carotte', emoji: '🥕', season: 'Toute l\'année' },
      { name: 'Pomme de Terre', emoji: '🥔', season: 'Toute l\'année' },
      { name: 'Poivron', emoji: '🫑', season: 'Toute l\'année' },
      { name: 'Aubergine', emoji: '🍆', season: 'Avril - Oct' },
      { name: 'Courgette', emoji: '🥒', season: 'Mai - Sept' },
      { name: 'Chou', emoji: '🥬', season: 'Toute l\'année' },
      { name: 'Salade', emoji: '🥗', season: 'Toute l\'année' },
      { name: 'Ail', emoji: '🧄', season: 'Toute l\'année' },
    ]
  },
  en: {
    fruits: [
      { name: 'Kent Mango', emoji: '🥭', season: 'March - June' },
      { name: 'Sweet Pineapple', emoji: '🍍', season: 'Year-round' },
      { name: 'Cavendish Banana', emoji: '🍌', season: 'Year-round' },
      { name: 'Solo Papaya', emoji: '🍈', season: 'Year-round' },
      { name: 'Hass Avocado', emoji: '🥑', season: 'Sept - Feb' },
      { name: 'Navel Orange', emoji: '🍊', season: 'Nov - April' },
      { name: 'Golden Apple', emoji: '🍎', season: 'Sept - March' },
      { name: 'Grape', emoji: '🍇', season: 'June - Oct' },
      { name: 'Watermelon', emoji: '🍉', season: 'March - Sept' },
      { name: 'Charentais Melon', emoji: '🍈', season: 'May - Sept' },
      { name: 'Lemon', emoji: '🍋', season: 'Year-round' },
      { name: 'Coconut', emoji: '🥥', season: 'Year-round' },
    ],
    legumes: [
      { name: 'Tomato', emoji: '🍅', season: 'Year-round' },
      { name: 'Onion', emoji: '🧅', season: 'Year-round' },
      { name: 'Carrot', emoji: '🥕', season: 'Year-round' },
      { name: 'Potato', emoji: '🥔', season: 'Year-round' },
      { name: 'Bell Pepper', emoji: '🫑', season: 'Year-round' },
      { name: 'Eggplant', emoji: '🍆', season: 'April - Oct' },
      { name: 'Zucchini', emoji: '🥒', season: 'May - Sept' },
      { name: 'Cabbage', emoji: '🥬', season: 'Year-round' },
      { name: 'Lettuce', emoji: '🥗', season: 'Year-round' },
      { name: 'Garlic', emoji: '🧄', season: 'Year-round' },
    ]
  }
};

const Produits = () => {
  const { language } = useLanguage();
  const t = language === 'fr';
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const data = products[language];
  const allProducts = [
    ...data.fruits.map(p => ({ ...p, category: 'fruit' })),
    ...data.legumes.map(p => ({ ...p, category: 'legume' }))
  ];

  const filteredProducts = allProducts.filter(p => {
    const matchesCategory = activeCategory === 'all' || p.category === activeCategory;
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const isYearRound = (season) => 
    season.includes('Toute l\'année') || season.includes('Year-round');

  return (
    <div className="min-h-screen bg-[#F8F2E8] dark:bg-[#0F1A14]">
      {/* Header */}
      <section className="bg-[#1A5C38] py-12 md:py-16">
        <div className="container text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            {t ? 'Nos Produits' : 'Our Products'}
          </h1>
          <p className="text-white/80 max-w-xl mx-auto">
            {t 
              ? 'Fruits et légumes frais, sélectionnés pour leur qualité et disponibles à Abidjan.'
              : 'Fresh fruits and vegetables, selected for quality and available in Abidjan.'}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white dark:bg-[#1A2B22] border-b border-gray-200 dark:border-gray-700 sticky top-16 md:top-20 z-40">
        <div className="container">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder={t ? 'Rechercher...' : 'Search...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-200 dark:border-gray-700 dark:bg-[#0F1A14] dark:text-white focus:outline-none focus:border-[#1A5C38]"
              />
            </div>

            {/* Category Tabs */}
            <div className="flex gap-2">
              {[
                { id: 'all', label: t ? 'Tous' : 'All' },
                { id: 'fruit', label: t ? 'Fruits' : 'Fruits' },
                { id: 'legume', label: t ? 'Légumes' : 'Vegetables' }
              ].map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeCategory === cat.id
                      ? 'bg-[#1A5C38] text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-8 md:py-12">
        <div className="container">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-white dark:bg-[#1A2B22] rounded-2xl p-4 text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="text-4xl mb-3">{product.emoji}</div>
                <h3 className="font-semibold text-[#1A5C38] dark:text-white text-sm mb-1">{product.name}</h3>
                <div className={`text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${
                  isYearRound(product.season)
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                }`}>
                  {isYearRound(product.season) && <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />}
                  {product.season}
                </div>
              </motion.div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              {t ? 'Aucun produit trouvé' : 'No products found'}
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-12 bg-white dark:bg-[#1A2B22]">
        <div className="container text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t ? 'Vous êtes un professionnel ? Demandez un devis personnalisé.' : 'Are you a professional? Request a custom quote.'}
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-[#1A5C38] hover:bg-[#145230] text-white font-semibold px-6 py-3 rounded-full transition-all"
          >
            {t ? 'Nous contacter' : 'Contact us'}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Produits;
